import { getWindowSize } from "../../common/html";
import { getPhotoStoreAsync, getPhotoStoresAsync, IPhotoStore, StoreType } from "../../services/storageService";
import { getCookie } from "../../utils/cookie";
import { getParameter } from "../../utils/querystring";
import { waitAsync } from "../../utils/wait";
import { CreateLoginRegister } from "./login-register";
import { createPopup } from "./popup-new";

export async function connectToAsync(
	store: IPhotoStore,
	connectedToStoreAsync: (store: IPhotoStore | null) => Promise<void>,
): Promise<void> {
	if (store.IsConnected && store.HasAlbumAccess) {
		await connectedToStoreAsync(store);
		return;
	}
	if (!store.LocatePhotosUrl) {
		await connectToSmartphotoStoreAsync(store, connectedToStoreAsync);
		return;
	}

	await connectToPartnerStoreAsync(store, connectedToStoreAsync);
}

export async function connectToSmartphotoStoreAsync(
	store: IPhotoStore,
	connectedToStoreAsync: (store: IPhotoStore | null) => Promise<void>,
): Promise<void> {
	const loginPopup = await createPopup({
		dialogId: "LoginRegisterComponent",
		view: "Authentication",
		data: { Email: getParameter("Email"), Mode: "login" },
		forceRefresh: true,
	});

	if (!loginPopup.popup) return undefined;

	window.loginWatcher && window.loginWatcher.cancel();
	CreateLoginRegister(loginPopup.popup, {
		onSuccess: async () => {
			const accName = getCookie("accName");
			if (accName != null && getCookie("acctype") == "Full") {
				store.IsConnected = true;
				store.HasAlbumAccess = true;
				store.ConnectedUserName = accName;
			}

			await connectedToStoreAsync(store);
			loginPopup.close();
		},
		closeLoginPopup: () => {
			loginPopup.close();
		},
	});

	await loginPopup.openAsync();
}

let partnerWindow: Window | null = null;
export async function connectToPartnerStoreAsync(
	store: IPhotoStore,
	connectedToStoreAsync: (store: IPhotoStore | null) => Promise<void>,
): Promise<void> {
	const url = store.LocatePhotosUrl + "?inpopup";
	const windowSize = getWindowSize();

	if (!partnerWindow || partnerWindow.closed)
		partnerWindow = window.open(
			url,
			"partnerWindow",
			`left=${windowSize.width > 1200 ? (windowSize.width - 1200) / 2 : 0},top=0,height=${windowSize.height},width=1200,status=no,toolbar=no,menubar=no,location=no,resizable=yes,scrollbars=yes`,
		);
	else partnerWindow.focus();

	const res = await waitForPartnerWindowCloseAsync();
	const photostores = await getPhotoStoresAsync(window.context.pointOfSaleId, window.context.language);
	const selectedstore = photostores ? photostores.find((s) => s.StoreType === store.StoreType) : undefined;
	if (selectedstore && !selectedstore.IsConnected && res) {
		// seems we are not connected yet, start polling
		// EDGE: the partner window might say it's closed while it's actually still open on partner site
		waitAsync(5000).then(() => {
			retryGetConnectedPhotoStore(selectedstore, 10);
		});
	}

	await connectedToStoreAsync(selectedstore);
}

export async function retryGetConnectedPhotoStoreByStoreType(
	storeType: StoreType,
	maxNumberOfRetry: number,
	retry = 0,
): Promise<IPhotoStore | undefined> {
	const photosource = await getPhotoStoreAsync(storeType.toString());
	if (!photosource) return undefined;
	if (!photosource.IsConnected && retry < maxNumberOfRetry) {
		await waitAsync(2000);
		return retryGetConnectedPhotoStoreByStoreType(storeType, maxNumberOfRetry, retry + 1);
	}
	if (photosource.IsConnected) {
		return photosource;
	}
	return undefined;
}

export function retryGetConnectedPhotoStore(
	selectedStore: IPhotoStore,
	maxNumberOfRetry: number,
	retry = 0,
): Promise<IPhotoStore | undefined> {
	return getPhotoStoresAsync(
		window.context.pointOfSaleId,
		window.context.language,
		false,
		false,
		false,
		maxNumberOfRetry,
		retry,
	).then((photostores) => {
		const store: IPhotoStore | undefined = photostores
			? photostores.find((s) => s.Code === selectedStore.Code)
			: undefined;
		if (!store) return undefined;

		if (!store.IsConnected && retry < maxNumberOfRetry) {
			return waitAsync(2000).then(() => retryGetConnectedPhotoStore(selectedStore, maxNumberOfRetry, retry + 1));
		} else if (store.IsConnected) {
			return store;
		}

		return undefined;
	});
}

export function waitForPartnerWindowCloseAsync(
	delay = 100,
	counter = 1,
	leftDomain = false,
	maxDelay = 60000,
): Promise<boolean> {
	let factor;
	if (counter % 10 === 0) {
		factor = counter / 10;
		delay = 100 * factor;
	}

	if (delay > maxDelay) delay = maxDelay;

	counter++;

	return waitAsync(delay).then((res) => {
		try {
			// here we check if the partner window is still open or not
			// warning: on edge, any reference to the partnerwindow is lost (or not permitted)
			// as soon as we leave our domain => therefor: try -> catch
			if (
				(partnerWindow && partnerWindow.document && partnerWindow.document.domain === document.domain && leftDomain) ||
				(partnerWindow && partnerWindow.closed) ||
				!partnerWindow
			) {
				// few cases to get here:
				// - we are back on our own domain after we got redirected from the partner site
				// - the partner window was closed
				partnerWindow = null;
				return Promise.resolve(leftDomain);
			} else {
				// the partner window is still open
				return waitForPartnerWindowCloseAsync(delay, counter, leftDomain);
			}
		} catch (e) {
			if (e.name === "SecurityError") {
				// some browser nicely throw this error when we are trying to access
				// properties of the partnerwindow when the partnerwindow is showing the partner site
				// => wait until we are back on our domain (pass TRUE since we have left our domain)
				return waitForPartnerWindowCloseAsync(delay, counter, true);
			}

			// AAARCH!!! we probably are on edge browser :(
			// we get here as soon as the partnerwindow redirect to partner site
			// just stop checking here (else we get endless loop)
			// only way to get notified of successful connection is to start polling
			// the photostores and see if this store suddenly get connected
			return Promise.resolve(true);
		}
	});
}
